import { lazy, Suspense, useEffect, useState } from "react";
import { trackBookingConversion, BOOKING_URL } from "@/lib/trackConversion";
import { ArrowRight, Star, Shield } from "lucide-react";

const DentalAnimations = lazy(() => import("@/components/DentalAnimations"));
const DentalFinanceIllustration = lazy(() => import("@/components/DentalFinanceIllustration"));

const Hero = () => {
  const [showHeroEnhancements, setShowHeroEnhancements] = useState(false);

  useEffect(() => {
    const activate = () => setShowHeroEnhancements(true);

    if (typeof window === "undefined") return;

    if ("requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(activate, { timeout: 1200 });

      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = setTimeout(activate, 350);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <section className="relative gradient-hero min-h-[92vh] flex items-center justify-center overflow-hidden">
      {showHeroEnhancements ? (
        <Suspense fallback={null}>
          <DentalAnimations variant="hero" />
        </Suspense>
      ) : null}
      
      {/* Ambient background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-10 left-5 w-96 h-96 rounded-full blur-3xl"
          style={{ background: "radial-gradient(circle, hsl(210 90% 50% / 0.15), transparent)" }}
        />
        <div
          className="absolute bottom-20 right-10 w-[500px] h-[500px] rounded-full blur-3xl"
          style={{ background: "radial-gradient(circle, hsl(170 55% 45% / 0.12), transparent)" }}
        />
        {/* Bottom wave */}
        <svg className="absolute bottom-0 left-0 right-0 w-full" viewBox="0 0 1440 120" fill="none" preserveAspectRatio="none">
          <path d="M0,60 Q360,0 720,60 Q1080,120 1440,60 L1440,120 L0,120Z" fill="hsl(var(--background))" />
        </svg>
      </div>

      <div className="container mx-auto px-4 relative z-10 pt-28 pb-36">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          <div className="text-center lg:text-left flex-1 max-w-2xl">
            {/* Trust badge */}
            <div className="inline-flex items-center gap-2.5 bg-card/10 backdrop-blur-md border border-card/15 rounded-full px-5 py-2.5 mb-8">
              <Shield className="w-4 h-4 text-accent" aria-hidden="true" />
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" aria-hidden="true" />
                ))}
              </div>
              <span className="text-sm text-primary-foreground/80 font-medium font-body">
                5.0 Google Rating · ACCA Certified
              </span>
            </div>

            <h1
              className="text-4xl md:text-5xl lg:text-[3.5rem] xl:text-6xl font-bold text-primary-foreground leading-[1.1] font-heading tracking-tight"
            >
              Specialist Dental Accountants, Save Tax, Keep More Money
            </h1>
            <p className="mt-7 text-lg md:text-xl text-primary-foreground/75 max-w-xl mx-auto lg:mx-0 leading-relaxed font-body font-light">
              Specialist accountants for dentists across the UK. Tax returns, dental bookkeeping, and practice accounts handled by a dental accountant who knows your numbers, so you can focus on patients, not paperwork.
            </p>

            <div className="flex flex-col sm:flex-row items-center lg:items-start gap-4 mt-10">
              <a
                href={BOOKING_URL}
                onClick={trackBookingConversion}
                className="btn-cta-light group text-base"
              >
                Get in Touch
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </a>
            </div>

            <p className="mt-5 text-sm text-primary-foreground/50 font-body">
              Free 30-minute call · No obligation · Trusted by dental professionals nationwide
            </p>

            <p className="mt-3 text-sm text-primary-foreground/60 font-body">
              Serving dentists across London, Manchester, Birmingham, Leeds and the rest of the UK.
            </p>
          </div>

          {/* Animated illustration */}
          {showHeroEnhancements ? (
            <div className="flex-shrink-0 scale-75 md:scale-100" aria-hidden="true">
              <Suspense
                fallback={<div className="w-80 h-80 md:w-[420px] md:h-[420px] lg:w-[480px] lg:h-[480px] mx-auto rounded-[2rem] bg-card/5 border border-card/10" />}
              >
                <DentalFinanceIllustration />
              </Suspense>
            </div>
          ) : (
            <div className="flex-shrink-0 scale-75 md:scale-100" aria-hidden="true">
              <div className="w-80 h-80 md:w-[420px] md:h-[420px] lg:w-[480px] lg:h-[480px] mx-auto rounded-[2rem] bg-card/5 border border-card/10" />
            </div>
          )}
        </div>

        {/* Scroll indicator */}
        {showHeroEnhancements ? (
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2">
            <div className="w-6 h-10 border-2 border-primary-foreground/25 rounded-full flex justify-center pt-2">
              <div className="w-1.5 h-1.5 bg-primary-foreground/50 rounded-full animate-bounce" />
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default Hero;
